import React from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Tooltip } from '@mui/material';
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';
import { BiDownload } from 'react-icons/bi';
import { importNpiAndTin } from '../../../../Redux/npiandtin/action';

const ImportNpiTinModal = ({ show, row, onClose, onImported }) => {
    const [importError, setImportError] = React.useState("");
    const [importResult, setImportResult] = React.useState(null);

    const importFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            file: "",
        },
        validationSchema: Yup.object().shape({
            file: Yup.mixed().required('Please select a file'),
        }),
        onSubmit: (values, { setSubmitting }) => {
            setSubmitting(true);
            setImportError("");
            setImportResult(null);
            const formData = new FormData();
            formData.append('file', values?.file[0]);
            formData.append('facility_id', row?.id);
            importNpiAndTin({ formData })((response) => {
                setSubmitting(false);
                if (response?.status) {
                    const result = response?.data || {};
                    onImported && onImported();
                    setImportResult(result);
                    importFormik.resetForm();
                } else {
                    setImportError(response?.response?.data?.message || "Import failed. Please check the file and try again.");
                }
            });
        },
    });

    const handleClose = () => {
        if (importFormik.isSubmitting) return;
        importFormik.resetForm();
        setImportError("");
        setImportResult(null);
        onClose && onClose();
    };

    const handleFileChange = (event) => {
        importFormik.setFieldValue('file', event.target.files);
        setImportResult(null);
        setImportError("");
    };

    const selectedFileName = importFormik.values?.file?.[0]?.name;

    const handleDownloadSample = () => {
        const sampleData = [{
            'Facility ID': row?.id ?? '',
            'Partner': row?.partner ?? '',
            'Client': row?.client ?? '',
            'Facility': row?.facility ?? '',
            'TIN': '',
            'TIN Status': 'Active',
            'NPI': '',
            'NPI Status': 'Active',
        }];
        const worksheet = utils.json_to_sheet(sampleData);
        const instructionsSheet = utils.aoa_to_sheet([
            ['Import Instructions'],
            ['1. Do not change the values in the "Facility ID", "Partner", "Client" or "Facility" columns.'],
            ['2. "TIN Status" and "NPI Status" only accept "Active" or "Inactive".'],
            ['3. Fill in "TIN" and/or "NPI" as needed; leave a value blank to skip updating it.'],
        ]);
        instructionsSheet['!cols'] = [{ wch: 95 }];
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, 'Sample');
        utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');
        const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const sanitize = (value) => (value || '').toString().trim().replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const nameParts = [sanitize(row?.client), sanitize(row?.facility)].filter(Boolean);
        const fileName = nameParts.length ? `NPI-TIN-Sample-${nameParts.join('-')}.xlsx` : 'NPI-TIN-Sample.xlsx';
        saveAs(blob, fileName);
    };


    return (
        <Modal show={show} onHide={handleClose} centered size="lg" backdrop={importFormik.isSubmitting ? 'static' : true}>
            <Modal.Header closeButton={!importFormik.isSubmitting}>
                <Modal.Title>Import NPI &amp; TIN</Modal.Title>
                <Tooltip disableInteractive title="Download a sample import file" placement='top' arrow>
                    <span>
                        <Button
                            variant='light'
                            size='sm'
                            className='btn-active-primary d-flex align-items-center gap-1 ms-3'
                            disabled={importFormik.isSubmitting}
                            onClick={handleDownloadSample}
                            type='button'
                        >
                            <BiDownload style={{ fontSize: '1rem' }} />
                            <span style={{ fontSize: '0.85rem' }}>Sample</span>
                        </Button>
                    </span>
                </Tooltip>
            </Modal.Header>
            <Form onSubmit={importFormik.handleSubmit}>
                <Modal.Body className='position-relative'>
                    {importFormik.isSubmitting &&
                        <div
                            className='position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-2'
                            style={{ backgroundColor: 'rgba(255,255,255,0.85)', zIndex: 5, borderRadius: '6px' }}
                        >
                            <div className='spinner-border color-xtract' role='status'>
                                <span className='visually-hidden'>Loading...</span>
                            </div>
                            <span className='fw-semibold text-muted'>Importing, please wait...</span>
                        </div>
                    }

                    <div className='p-3 mb-4' style={{ backgroundColor: 'hsl(204deg 33.33% 97.06%)', borderRadius: '10px' }}>
                        <div className='d-flex flex-column gap-2'>
                            <div className='d-flex align-items-baseline gap-2 flex-wrap'>
                                <span className='text-muted text-uppercase flex-shrink-0' style={{ fontSize: '0.72rem', letterSpacing: '0.03em', width: '70px' }}>Partner</span>
                                <span className='fw-semibold'>{row?.partner || '-'}</span>
                            </div>
                            <div className='d-flex align-items-baseline gap-2 flex-wrap'>
                                <span className='text-muted text-uppercase flex-shrink-0' style={{ fontSize: '0.72rem', letterSpacing: '0.03em', width: '70px' }}>Client</span>
                                <span className='fw-semibold'>{row?.client || '-'}</span>
                            </div>
                            <div className='d-flex align-items-baseline gap-2 flex-wrap'>
                                <span className='text-muted text-uppercase flex-shrink-0' style={{ fontSize: '0.72rem', letterSpacing: '0.03em', width: '70px' }}>Facility</span>
                                <span className='fw-semibold'>{row?.facility || '-'}</span>
                            </div>
                        </div>
                    </div>

                    <label className='fw-semibold mb-1'>Upload File</label>
                    <div className='text-muted mb-2' style={{ fontSize: '0.8rem' }}>
                        Do not change the <strong>Facility ID, Partner, Client</strong> or <strong>Facility</strong> columns. <strong>TIN Status</strong> and <strong>NPI Status</strong> accept only <strong>Active</strong> or <strong>Inactive</strong>.
                    </div>
                    <div
                        className='d-flex align-items-center gap-3 p-3'
                        style={{
                            border: `1px dashed ${importFormik.errors?.file  && importFormik.submitCount > 0  ? '#dc3545' : '#c9d3dc'}`,
                            borderRadius: '10px',
                            backgroundColor: '#fbfcfd',
                        }}
                    >
                        <div
                            className='d-flex align-items-center justify-content-center flex-shrink-0'
                            style={{ width: '42px', height: '42px', borderRadius: '50%', backgroundColor: 'hsl(204deg 33.33% 93%)' }}
                        >
                            <i className='bi bi-file-earmark-arrow-up' style={{ fontSize: '1.2rem' }}></i>
                        </div>
                        <div className='flex-grow-1 overflow-hidden'>
                            <Form.Control
                                type="file"
                                accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                className='form-control form-control-sm border-0 px-0'
                                disabled={importFormik.isSubmitting}
                                onChange={handleFileChange}
                                onBlur={() => importFormik.setFieldTouched('file', true)}
                                name="file"
                                style={{ backgroundColor: 'transparent' }}
                            />
                            {selectedFileName &&
                                <div className='text-success d-flex align-items-center gap-1 mt-1' style={{ fontSize: '0.85rem' }}>
                                    <i className='bi bi-check-circle-fill'></i>
                                    <span className='text-truncate'>{selectedFileName}</span>
                                </div>
                            }
                        </div>
                    </div>
                    {importFormik.submitCount > 0 && importFormik.errors?.file && (
                        <div className='text-danger mt-1' style={{ fontSize: '0.85rem' }}>
                            {importFormik.errors.file}
                        </div>
                    )}

                    {!!importError &&
                        <div className='alert alert-danger d-flex align-items-center gap-2 mt-3 mb-0 py-2'>
                            <i className='bi bi-exclamation-triangle-fill'></i>
                            <span>{importError}</span>
                        </div>
                    }

                    {!!importResult &&
                        <div className='mt-3'>
                            <div className='d-flex flex-wrap gap-4 mb-2 p-3' style={{ backgroundColor: 'hsl(204deg 33.33% 97.06%)', borderRadius: '10px' }}>
                                <div>
                                    <div className='text-muted text-uppercase' style={{ fontSize: '0.72rem', letterSpacing: '0.03em' }}>Facilities Processed</div>
                                    <div className='fw-bold' style={{ fontSize: '1.1rem' }}>{importResult?.facilities_processed ?? 0}</div>
                                </div>
                                <div>
                                    <div className='text-muted text-uppercase' style={{ fontSize: '0.72rem', letterSpacing: '0.03em' }}>TIN Created / Updated</div>
                                    <div className='fw-bold' style={{ fontSize: '1.1rem' }}>{importResult?.tin_created ?? 0}</div>
                                </div>
                                <div>
                                    <div className='text-muted text-uppercase' style={{ fontSize: '0.72rem', letterSpacing: '0.03em' }}>NPI Created / Updated</div>
                                    <div className='fw-bold' style={{ fontSize: '1.1rem' }}>{importResult?.npi_created ?? 0}</div>
                                </div>
                            </div>

                            {importResult?.errors?.length > 0 &&
                                <div className='alert alert-warning mb-2 py-2 px-3'>
                                    <div className='d-flex align-items-center gap-2 fw-semibold mb-1'>
                                        <i className='bi bi-exclamation-triangle-fill'></i>
                                        <span>{importResult?.errors?.length} row{importResult?.errors?.length === 1 ? '' : 's'} could not be imported</span>
                                    </div>
                                    <ul className='mb-0 ps-3' style={{ maxHeight: '160px', overflowY: 'auto', fontSize: '0.85rem' }}>
                                        {importResult?.errors?.map((err, idx) => (
                                            <li key={idx}>{err}</li>
                                        ))}
                                    </ul>
                                </div>
                            }

                            {importResult?.already_exists?.length > 0 &&
                                <div className='alert alert-info mb-2 py-2 px-3'>
                                    <div className='d-flex align-items-center gap-2 fw-semibold mb-1'>
                                        <i className='bi bi-info-circle-fill'></i>
                                        <span>{importResult?.already_exists?.length} row{importResult?.already_exists?.length === 1 ? '' : 's'} skipped — already exists</span>
                                    </div>
                                    <ul className='mb-0 ps-3' style={{ maxHeight: '160px', overflowY: 'auto', fontSize: '0.85rem' }}>
                                        {importResult?.already_exists?.map((msg, idx) => (
                                            <li key={idx}>{msg}</li>
                                        ))}
                                    </ul>
                                </div>
                            }

                            {(!importResult?.errors?.length && !importResult?.already_exists?.length) &&
                                <div className='alert alert-success d-flex align-items-center gap-2 mb-0 py-2'>
                                    <i className='bi bi-check-circle-fill'></i>
                                    <span>Import completed successfully.</span>
                                </div>
                            }
                        </div>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" disabled={importFormik.isSubmitting} onClick={handleClose}>
                        Close
                    </Button>
                    <Button className='btn btn-primary btnHover d-flex align-items-center gap-2' disabled={importFormik.isSubmitting} variant="primary" type="submit">
                        {importFormik.isSubmitting &&
                            <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                        }
                        {importFormik.isSubmitting ? 'Importing...' : 'Import'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ImportNpiTinModal;
